import { useState } from "react";

import { SearchMessageHit } from "./searchApi";

export interface SearchResultsListProps {
  hits: SearchMessageHit[];
  loading: boolean;
  loadingMore: boolean;
  empty: boolean;
  error: string | null;
  totalHits: number;
  canLoadMore: boolean;
  onLoadMore: () => Promise<void>;
  onOpenResult: (hit: SearchMessageHit) => Promise<void>;
}

export function getSearchHitKey(hit: SearchMessageHit): string {
  return `${hit.chat_id}:${hit.message_id}`;
}

export function getSearchHitLabel(hit: SearchMessageHit): string {
  const label = hit.conversation_label?.trim() ?? "";
  return label === "" ? "Message" : label;
}

export function SearchResultsList(props: SearchResultsListProps) {
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const onOpen = async (hit: SearchMessageHit) => {
    const key = getSearchHitKey(hit);
    setOpeningKey(key);
    try {
      await props.onOpenResult(hit);
    } finally {
      setOpeningKey((current) => (current === key ? null : current));
    }
  };

  return (
    <div className="search-results-block" data-testid="search-results">
      {props.loading ? <p className="muted-line">Searching messages...</p> : null}
      {props.error !== null ? (
        <p className="inline-error" data-testid="search-results-error">
          {props.error}
        </p>
      ) : null}
      {!props.loading && props.empty ? (
        <p className="muted-line" data-testid="search-results-empty">
          No matches found. Try another phrase.
        </p>
      ) : null}
      {!props.loading && props.hits.length > 0 ? (
        <>
          <p className="muted-line search-results-meta" data-testid="search-results-meta">
            {props.hits.length} of {props.totalHits} matches
          </p>
          <ul className="search-results-list">
            {props.hits.map((hit) => {
              const key = getSearchHitKey(hit);
              const opening = openingKey === key;
              return (
                <li key={key}>
                  <article className="search-result-card" data-testid={`search-hit-${hit.message_id}`}>
                    <header className="search-result-header">
                      <p className="search-result-title">{getSearchHitLabel(hit)}</p>
                    </header>
                    <p className="search-result-snippet">{hit.snippet || "No snippet available."}</p>
                    <button
                      className="secondary-button"
                      type="button"
                      data-testid={`open-search-hit-${hit.message_id}`}
                      onClick={() => void onOpen(hit)}
                      disabled={opening}
                    >
                      {opening ? "Opening..." : "Open in context"}
                    </button>
                  </article>
                </li>
              );
            })}
          </ul>
          {props.canLoadMore ? (
            <button
              className="secondary-button"
              type="button"
              data-testid="search-load-more"
              onClick={() => void props.onLoadMore()}
              disabled={props.loadingMore}
            >
              {props.loadingMore ? "Loading..." : "Load more"}
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
